import { useContext, useState } from "react";

import { Condition } from "@medplum/fhirtypes";

import { Button, Group, Paper, Stack, Text, Title } from "@mantine/core";

import { IconPlus, IconReportMedical } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { ProfileContext } from "@/contexts/profile";
import { useHasEncounterChildSave } from "@/encounters/contexts/EncounterChildSaveContext";
import { ProceduresEditTarget } from "@/encounters/types/procedures";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";

import { DiagnosisRemovalModal } from "../diagnosis-removal";
import { NoDataContainer } from "../NoDataContainer";

import EncounterDiagnosisCard from "./components/EncounterDiagnosisCard";
import useDiagnosisProblemList from "./hooks/useDiagnosisProblemList";
import useEncounterDiagnoses, { UseEncounterDiagnoses } from "./hooks/useEncounterDiagnoses";

interface EncounterDiagnosesProps {
  isEncounterActive: boolean;
  isAmendment?: boolean;
  /**
   * Makes Procedures the active tab, which is where the encounter's diagnoses are added and edited.
   *
   * Absent in the shells that have no Procedures tab — a labor encounter's Dx tab and an
   * amendment's — so the controls that would have nowhere to go are not offered there.
   */
  onOpenProceduresTab?: () => void;
}

const NO_DIAGNOSES_TEXT = "No diagnosis has been added.";

/** What the section says instead of cards, with the way in to add one when there is somewhere to go. */
const DiagnosesMessage = ({ text, onNewClick }: { text: string; onNewClick?: () => void }) => (
  <NoDataContainer
    text={text}
    hideAnchor={!onNewClick}
    linkText={onNewClick ? "New" : ""}
    onAnchorClick={() => onNewClick?.()}
    actionTestId="encounter-diagnoses-empty-new-button"
    icon={<IconReportMedical size={18} color="var(--mantine-color-gray-8)" />}
  />
);

type DiagnosesBodyProps = UseEncounterDiagnoses & {
  canEditDiagnoses: boolean;
  onOpenProceduresTab?: () => void;
  /**
   * Opens the removal confirmation on the resources behind the card that asked for it. Absent where
   * the shell never writes what the confirmation stages.
   */
  onRemove?: (target: ProceduresEditTarget) => void;
  /** Puts the diagnosis behind the card that asked on the client's problem list, or takes it off. */
  onToggleProblemList: (condition: Condition) => void;
  /** The diagnosis a problem-list write is in flight for, if any. */
  pendingProblemListConditionId: string | undefined;
};

/**
 * What the section shows under its header: the read, then what the read found.
 *
 * A failed read says so instead of falling through to the empty state — "nothing was recorded on
 * this encounter" and "we could not find out" are different things to tell a provider.
 */
function DiagnosesBody({
  isLoading,
  hasLoadFailed,
  diagnoses,
  canEditDiagnoses,
  onOpenProceduresTab,
  onRemove,
  onToggleProblemList,
  pendingProblemListConditionId,
}: DiagnosesBodyProps) {
  if (isLoading) return <Loading height={120} />;
  if (hasLoadFailed) return <DiagnosesMessage text="The diagnoses could not be loaded." />;

  if (!diagnoses.length) {
    // An encounter with nothing on it has no section-level New button to fall back on, so the empty
    // state is the only way in.
    if (!canEditDiagnoses || !onOpenProceduresTab)
      return <DiagnosesMessage text={NO_DIAGNOSES_TEXT} />;

    return (
      <PermissionGate
        fallback={<DiagnosesMessage text={NO_DIAGNOSES_TEXT} />}
        requiredPermissions={PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS}
      >
        <DiagnosesMessage text={NO_DIAGNOSES_TEXT} onNewClick={onOpenProceduresTab} />
      </PermissionGate>
    );
  }

  return (
    <Stack gap="md">
      {/* The card is handed what each action does, closed over the resources its group was built
          from, so it renders a diagnosis without ever seeing a FHIR resource. */}
      {diagnoses.map(({ resources, condition, procedures }) => (
        <EncounterDiagnosisCard
          condition={condition}
          procedures={procedures}
          onEdit={onOpenProceduresTab}
          canEditDiagnoses={canEditDiagnoses}
          key={condition.id ?? condition.code}
          onAddProcedure={onOpenProceduresTab}
          onDelete={onRemove && (() => onRemove(resources))}
          onToggleProblemList={() => onToggleProblemList(resources.condition)}
          isProblemListPending={pendingProblemListConditionId === condition.id}
        />
      ))}
    </Stack>
  );
}

/**
 * The Dx tab's Diagnoses section, read from the Condition resources on the encounter.
 *
 * One card per diagnosis, with the procedures recorded under it — the encounter's clinical record as
 * the procedures picker wrote it, rather than the diagnoses held in the encounter answers form. It
 * replaces the form-driven list when the billing pipeline flags are on; see DxPlanTabPanel.
 *
 * New, Edit and Add Procedure take the provider to the Procedures tab rather than editing here, and
 * Delete opens the same confirmation that tab does, staging onto the same save — so a removal
 * confirmed here is gone from both tabs at once. Add to Problem List is the exception that writes
 * immediately: it changes the client's record, not the encounter's.
 */
function EncounterDiagnoses({
  isEncounterActive,
  isAmendment,
  onOpenProceduresTab,
}: EncounterDiagnosesProps) {
  const state = useEncounterDiagnoses();
  const { isPatient } = useContext(ProfileContext);
  const { pendingConditionId, toggleProblemList } = useDiagnosisProblemList();
  const hasEncounterChildSave = useHasEncounterChildSave();
  // The diagnosis the removal confirmation is open for, with the procedures recorded under it.
  const [removalTarget, setRemovalTarget] = useState<ProceduresEditTarget | null>(null);

  // Matches the existing list: a locked encounter shows its diagnoses but offers no actions on them.
  // A client sees their own diagnoses but never acts on them, regardless of the encounter's state.
  const canEditDiagnoses = (isEncounterActive || Boolean(isAmendment)) && !isPatient;

  // Delete only stages; the encounter's child save is what writes it. A shell without one would
  // confirm a removal, say it is unsaved, and then never write it. Offered wherever the shell runs
  // a child save, amendments included.
  const canRemoveDiagnoses = canEditDiagnoses && hasEncounterChildSave;

  return (
    <Paper p={24} radius="lg" data-testid="encounter-diagnoses">
      <Group pb="md" align="center" justify="space-between">
        <Group gap="sm" align="center">
          <Title order={3} c="burgundy.9">
            Diagnoses
          </Title>
          {/* The same hint the Procedures tab shows, off the same staged set. */}
          {state.hasStagedChanges && (
            <Text fz="sm" c="gray.7" data-testid="encounter-diagnoses-unsaved-changes">
              Unsaved — these are written when you save the encounter
            </Text>
          )}
        </Group>
        {state.diagnoses.length > 0 && canEditDiagnoses && onOpenProceduresTab && (
          <PermissionGate requiredPermissions={PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS}>
            <Button
              fw={600}
              variant="outline"
              color="burgundy.9"
              onClick={onOpenProceduresTab}
              data-testid="encounter-diagnoses-new-button"
              leftSection={<IconPlus size={16} color="var(--mantine-color-burgundy-9)" />}
            >
              New
            </Button>
          </PermissionGate>
        )}
      </Group>

      <DiagnosesBody
        {...state}
        canEditDiagnoses={canEditDiagnoses}
        onToggleProblemList={toggleProblemList}
        onOpenProceduresTab={onOpenProceduresTab}
        pendingProblemListConditionId={pendingConditionId}
        onRemove={canRemoveDiagnoses ? setRemovalTarget : undefined}
      />

      <DiagnosisRemovalModal target={removalTarget} onClose={() => setRemovalTarget(null)} />
    </Paper>
  );
}

export default EncounterDiagnoses;
